import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import Head from "../../../common/components/Head/Head";
import GoBackButton from "../../../common/components/Buttons/GoBackButton";
import LoadingSpinner from "../../../common/components/LoadingSpinner";
import BidDetails from "./BidDetails";
import { useTranslation } from "react-i18next";
import Timeline from "./Timeline";
import { routes } from "../../../routes";
import Sections from "../../../common/components/Sections/Sections";
import Related from "./Related";
import MusicDetails from "./MusicDetails";
import MusicAbout from "./MusicAbout";
import AssetContentLayout from "./AssetContentLayout";
import { scrollToElement } from "../../../helpers/scroll";
import GlobalPlayerCard from "../../../common/components/GlobalPlayerCard/GlobalPlayerCard";
import { IMAGE_SIZES } from "../../../constants/imageSizes";
import {
  useAssetDetailsQuery,
  useRelatedTracksQuery,
} from "../../../queries/asset.query";
import NoResults from "../../../common/components/NoResults/NoResults";
import { useSession } from "../../../queries/authentication.query";
import { getQueryParam } from "../../../helpers/nextHelper";
import { getAssetPageMetadata } from "../../../helpers/shareMetadataHelper";
import { IMusic } from "../../../common/commonTypes";
import { useThemeCustomizer } from "../../../context/ThemeProvider";
import { Paragraph14Bold } from "../../../common/Typography/Paragraph14";
import Card from "../../../common/components/Card/Card";

const Root = styled.div`
  padding-bottom: 26px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobileL}) {
    padding-bottom: 24px;
  }
`;

const Container = styled.div`
  margin: 0 auto;
  padding-top: 20px;
  position: relative;
`;

const LoadingContainer = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 640px;
`;

const StyledBackButton = styled(GoBackButton)`
  margin-bottom: 84px;
`;

const BidDetailsContent = styled.div`
  position: sticky;
  top: 142px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: relative;
    top: 0;
  }
`;

const EmptyLabel = styled(Paragraph14Bold)`
  color: ${(props) => props.theme.greyColor};
`;

const Asset = () => {
  const { t } = useTranslation();
  const { user } = useSession();
  const router = useRouter();
  const musicId = getQueryParam(router.query, "musicId")!;
  const listingId = getQueryParam(router.query, "listing_id");
  const [activeSectionId, setActiveSectionId] = useState<string | number>();
  const { data, isLoading, error, refetch } = useAssetDetailsQuery(
    musicId,
    listingId
  );
  const { music, listing, nft, bids } = data || {};
  const { data: playlist = [] } = useRelatedTracksQuery(
    music || ({ id: musicId } as IMusic),
    !!music
  );
  const musicInfoRef = useRef<HTMLDivElement>(null);

  useThemeCustomizer(music?.artist);

  useEffect(() => {
    if (music && router.asPath.includes("#history") && musicInfoRef.current) {
      setActiveSectionId("history");
      scrollToElement(musicInfoRef.current);
    }
  }, [music, router.asPath]);

  useEffect(() => {
    if (music?.hidden && user?.id !== music?.artist_id) {
      router.replace(routes.page404);
    }
  }, [user, music]);

  const handleOnSuccess = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <LoadingContainer className="main-container">
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  const renderContent = () => {
    if (error) {
      return (
        <NoResults
          title={t("something-went-wrong")}
          subtitle={t("working-on-a-fix")}
        />
      );
    }

    if (!music) {
      return null;
    }

    const sections = [
      {
        id: "about",
        title: t("asset-tabs-about"),
        content: <MusicAbout music={music} />,
      },
      {
        id: "info",
        title: t("asset-tabs-information"),
        content: <MusicDetails music={music} nft={nft} listing={listing} />,
      },
      {
        id: "history",
        title: t("asset-tabs-timeline"),
        content: listing ? (
          <Timeline bids={bids} listing={listing!} />
        ) : (
          <Card>
            <EmptyLabel>{t("timeline-empty-message")}</EmptyLabel>
          </Card>
        ),
      },
    ];

    return (
      <AssetContentLayout
        player={
          <GlobalPlayerCard
            music={music}
            playlist={[music, ...playlist]}
            coverResizeWidth={IMAGE_SIZES.trackCoverArt.big}
          />
        }
        bidInfo={
          <BidDetailsContent>
            <BidDetails
              listing={listing}
              nft={nft}
              music={music}
              onSuccess={handleOnSuccess}
            />
          </BidDetailsContent>
        }
        musicInfo={
          <div ref={musicInfoRef}>
            <Sections
              sections={sections}
              activeSectionId={activeSectionId}
              setActiveSectionId={setActiveSectionId}
            />
            <Related music={music} artist={music?.artist} />
          </div>
        }
      />
    );
  };

  return (
    <Root>
      {music && <Head {...getAssetPageMetadata(music)} />}
      <Container className="main-container">
        <StyledBackButton onClick={() => router.back()} />
        {renderContent()}
      </Container>
    </Root>
  );
};
export default Asset;
